import { FC, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { IPsySetting } from '../../../store/psyTools.slice';
import { shuffleArray } from '../../../utils/textHelpers';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { clearMacClientSelections, clearMacSharedView } from '../../../store/mac.slice';

import { withHoverIcon } from '../../../hoc/withHoverIcon';
import { CloseCircleCrossBtn } from '../../icons/close-circle-cross-btn';
import { MacInfoIcon } from '../../icons/mac-info-icon';
import { ShuffleIcon } from '../../icons/shuffle';
import Button from '../../common/button';

import { DeckPreview } from './mac-card-psy';
import { MacDeckGalleryModal } from './mac-deck-gallery-modal';

const CloseCircleCrossBtnHover = withHoverIcon(CloseCircleCrossBtn);

const CLOSE_BTN_COLOR_PROPS = {
  defaultFill: '#828282',
  defaultFillBg: 'rgba(255, 255, 255, 0.8)',
  hoverFill: '#63656A',
  hoverFillBg: '#DEC05E',
};

const MAC_DECK_TOOLTIP_TEXT =
  'Выберите одну колоду из 25 для показа клиенту и способ ее отображения: рубашкой вверх или вниз';

export interface MacDeckSelectContentProps {
  onClose: () => void;
  onConfirm?: () => void;
  sendMacCardsOpen?: (data: {
    show: boolean;
    cover: string;
    color: string;
    result: { name: string; value: string[] };
  }) => void;
  className?: string;
  variant?: 'modal' | 'panel';
  /** Для variant="panel" на светлом фоне: тёмный текст и кнопка закрытия */
  panelLightTheme?: boolean;
  /** Панель развёрнута на всю высоту (только variant="panel") */
  isExpanded?: boolean;
  /** Переключить развёрнутое состояние (только variant="panel") */
  onExpandToggle?: () => void;
}

/**
 * Контент выбора колоды МАК без обёртки модалки.
 * Используется в MacDeckSelectModal (и при необходимости в панели групповой терапии).
 */
export const MacDeckSelectContent: FC<MacDeckSelectContentProps> = ({
  onClose,
  onConfirm,
  sendMacCardsOpen,
  className,
  variant = 'modal',
  panelLightTheme = false,
  isExpanded = false,
  onExpandToggle,
}) => {
  const dispatch = useAppDispatch();
  const psyTools = useAppSelector((state) => state.psyToolsSlice.tools);
  const cards = psyTools.find((el) => el.name === 'metaforic_cards')?.psy_tool_settings || [];

  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [coverDown, setCoverDown] = useState(false);
  const [inspectedDeck, setInspectedDeck] = useState<IPsySetting | null>(null);
  const [deckTooltipVisible, setDeckTooltipVisible] = useState(false);
  const [deckTooltipRect, setDeckTooltipRect] = useState<{ top: number; right: number } | null>(null);
  const deckInfoIconRef = useRef<HTMLDivElement>(null);

  const showDeckTooltip = () => {
    const rect = deckInfoIconRef.current?.getBoundingClientRect();
    if (rect) {
      setDeckTooltipRect({ top: rect.top, right: rect.right });
      setDeckTooltipVisible(true);
    }
  };
  const hideDeckTooltip = () => {
    setDeckTooltipVisible(false);
    setDeckTooltipRect(null);
  };

  const handleShowDeck = () => {
    if (!selectedCard) return;

    const selectedSettings = cards.find((el) => el.id === selectedCard)?.settings?.[0]?.value || [];
    const cover = selectedSettings[0] || '';
    const color = selectedSettings[1] || '';
    const selectedDeck = cards.find((el) => el.id === selectedCard);
    const deck = selectedDeck?.value || [];
    const payload = {
      show: coverDown,
      cover,
      color,
      result: {
        name: selectedDeck?.name || '',
        value: shuffleArray(Object.values(deck)),
      },
    };

    dispatch(clearMacClientSelections());
    dispatch(clearMacSharedView());
    sendMacCardsOpen?.(payload);
    dispatch(setActiveTool(PsyToolsId.MAC_CARDS));
    onConfirm?.();
    if (variant !== 'panel') {
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedCard(null);
    setInspectedDeck(null);
    onClose();
  };

  const isPanel = variant === 'panel';
  const isPanelLight = isPanel && panelLightTheme;

  return (
    <>
      <div
        className={classNames(
          'relative flex flex-col h-full min-h-0 overflow-hidden',
          isPanel ? 'gap-3 px-4 py-4' : 'gap-4 p-6 lg:p-8',
          className,
        )}
      >
        {isPanel ? (
          <div className="absolute top-5 right-5 flex items-center gap-2 z-[40] pointer-events-auto">
            {onExpandToggle && (
              <button
                type="button"
                onClick={onExpandToggle}
                className="pointer-events-auto flex items-center gap-1.5 text-[#2C3038] hover:text-[#585858] text-sm font-manrope px-2 py-1 rounded hover:bg-[#E5E5E5]"
                aria-label={isExpanded ? 'Свернуть панель' : 'Расширить панель'}
              >
                {isExpanded ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zm-3-12v2h3v3h2V5h-5z" />
                    </svg>
                    Свернуть
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zm-3-12v2h3v3h2V5h-5z" />
                    </svg>
                    Расширить
                  </>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="pointer-events-auto cursor-pointer"
              aria-label="Закрыть"
            >
              <CloseCircleCrossBtnHover width={30} height={30} className="" {...CLOSE_BTN_COLOR_PROPS} />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 flex-shrink-0">
            <h2 className="font-comfortaa font-bold text-[#111026] text-lg lg:text-2xl">МАК колоды</h2>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-grey1 hover:bg-grey2 transition-colors text-[#111026]"
              aria-label="Закрыть"
            >
              <CloseCircleCrossBtnHover width={20} height={20} {...CLOSE_BTN_COLOR_PROPS} />
            </button>
          </div>
        )}
        {isPanel && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              ref={deckInfoIconRef}
              className="shrink-0"
              onMouseEnter={showDeckTooltip}
              onMouseLeave={hideDeckTooltip}
            >
              <MacInfoIcon width={16} height={16} className="cursor-default" />
            </div>
            <h2 className="font-comfortaa font-bold text-[#111026] text-base">МАК колоды</h2>
          </div>
        )}
        {isPanel &&
          deckTooltipVisible &&
          deckTooltipRect &&
          createPortal(
            <div
              className="fixed z-[9999] w-[207px] rounded-lg px-3 py-2.5 text-sm text-white shadow-lg"
              style={{
                backgroundColor: '#2C3038',
                left: deckTooltipRect.right + 8,
                top: deckTooltipRect.top - 80,
              }}
              onMouseEnter={showDeckTooltip}
              onMouseLeave={hideDeckTooltip}
            >
              <p className="leading-snug">{MAC_DECK_TOOLTIP_TEXT}</p>
              <div
                className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent"
                style={{ borderRightColor: '#2C3038' }}
              />
            </div>,
            document.body,
          )}

        <div className={classNames('flex-1 min-h-0', isPanel && 'relative flex flex-col')}>
          <div
            className={classNames(
              'grid grid-cols-2 overflow-y-auto white-scroll',
              isPanel
                ? 'flex-1 min-h-0 gap-3 pr-1 w-full min-w-0 lg:grid-cols-6 pb-[100px]'
                : 'flex-1 min-h-0 gap-3 pr-1 lg:grid-cols-4',
            )}
          >
            {cards.map((el, idx) => (
              <DeckPreview
                key={el?.id ?? idx}
                deck={el}
                coverDown={coverDown}
                isSelected={selectedCard === el?.id}
                onSelect={(id) => setSelectedCard(id)}
                onInspect={(deck) => setInspectedDeck(deck)}
              />
            ))}
          </div>

          {isPanel ? (
            <div className="absolute z-10 bottom-4 left-0 right-0 flex justify-center items-center px-4 py-3 bg-white/70 backdrop-blur-md pointer-events-none">
              <div className="flex items-center gap-[20px] pointer-events-auto">
                <Button
                  className={classNames(
                    '!w-auto p-[5px] flex items-center justify-center border-transparent hover:border-blackBase hover:bg-grey3',
                    {
                      '!bg-[#DEC05E] hover:!bg-[#DEC05E] !border-[#DEC05E]': coverDown,
                    },
                  )}
                  type="grey"
                  onClick={() => setCoverDown(!coverDown)}
                >
                  <ShuffleIcon />
                </Button>
                <Button
                  className="!text-sm lg:!text-base p-[6px_10px]"
                  disabled={!selectedCard}
                  onClick={handleShowDeck}
                >
                  Показать колоду
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-[20px] pt-2 flex-shrink-0">
              <Button
                className={classNames(
                  '!w-auto p-[5px] flex items-center justify-center border-transparent hover:border-blackBase hover:bg-grey3',
                  {
                    '!bg-[#DEC05E] hover:!bg-[#DEC05E] !border-[#DEC05E]': coverDown,
                  },
                )}
                type="grey"
                onClick={() => setCoverDown(!coverDown)}
              >
                <ShuffleIcon />
              </Button>
              <Button className="!text-sm lg:!text-base p-[6px_10px]" disabled={!selectedCard} onClick={handleShowDeck}>
                Показать колоду
              </Button>
            </div>
          )}
        </div>
      </div>

      <MacDeckGalleryModal
        isOpen={!!inspectedDeck}
        deckName={inspectedDeck?.name}
        cards={Array.isArray(inspectedDeck?.value) ? (inspectedDeck?.value as string[]) : []}
        onClose={() => setInspectedDeck(null)}
      />
    </>
  );
};
